import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MenuConfig from "@/components/configuracion/MenuConfig";
import {
  SECCION_INICIAL,
  SECCION_POR_TAB_VIEJO,
  buscarSeccion,
} from "@/components/configuracion/secciones";
import AyudaVista from "@/ui/AyudaVista";
import { AYUDA_CONFIGURACION } from "@/components/ayuda/vistas/configuracion";

/** Debajo de `md` (768px) la navegación es de dos pantallas: lista → sección. */
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const alCambiar = (e: MediaQueryListEvent) => setEsMovil(e.matches);
    mq.addEventListener("change", alCambiar);
    return () => mq.removeEventListener("change", alCambiar);
  }, []);
  return esMovil;
}

export default function Configuracion() {
  const [params, setParams] = useSearchParams();
  const esMovil = useEsMovil();

  // ID de cliente a enfocar (deep-link desde el caso). Se captura UNA sola vez:
  // el autoOpen del picker es de un solo uso, así que limpiamos el ?cliente de
  // la URL para que un reload (Ctrl+Shift+R) NO lo vuelva a abrir solo.
  const [focusClienteId] = useState<number | undefined>(() => {
    const c = params.get("cliente");
    const n = c ? parseInt(c, 10) : NaN;
    return Number.isFinite(n) ? n : undefined;
  });

  useEffect(() => {
    if (!params.get("cliente")) return;
    const next = new URLSearchParams(params);
    next.delete("cliente");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compatibilidad con los enlaces viejos (?tab=). Ya nadie los genera, pero
  // pueden estar en un marcador. Se traducen y se reemplaza la URL en silencio.
  useEffect(() => {
    const tab = params.get("tab");
    if (!tab) return;
    const next = new URLSearchParams(params);
    next.delete("tab");
    const equivalente = SECCION_POR_TAB_VIEJO[tab];
    if (equivalente) next.set("seccion", equivalente);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idEnUrl = params.get("seccion");
  const seccionEnUrl = buscarSeccion(idEnUrl);

  function irA(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set("seccion", id);
    else next.delete("seccion");
    setParams(next);
  }

  const encabezado = (
    <div className="flex items-center gap-2">
      <h1 className="text-xl font-semibold">Configuración</h1>
      <AyudaVista titulo="Configuración" {...AYUDA_CONFIGURACION} />
    </div>
  );

  // Móvil: sin sección en la URL se ve la lista; con sección, la sección y un
  // camino de regreso. Al vivir en la URL, el botón «atrás» del navegador hace
  // lo que se espera sin código extra.
  if (esMovil) {
    if (!seccionEnUrl) {
      return (
        <div className="space-y-4">
          {encabezado}
          <MenuConfig actual={null} onElegir={(id) => irA(id)} variante="lista" />
        </div>
      );
    }
    const { titulo, Panel } = seccionEnUrl;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => irA(null)}
          className="btn-ghost -ml-2 text-sm text-zinc-400"
        >
          ‹ Configuración
        </button>
        <Panel key={seccionEnUrl.id} focusClienteId={focusClienteId} />
        <span className="sr-only">{titulo}</span>
      </div>
    );
  }

  const seccion = seccionEnUrl ?? buscarSeccion(SECCION_INICIAL)!;
  const { Panel } = seccion;

  return (
    <div className="space-y-4">
      {encabezado}
      <div className="grid gap-6 md:grid-cols-[9.5rem_minmax(0,1fr)] lg:grid-cols-[12.5rem_minmax(0,1fr)]">
        <aside className="md:sticky md:top-4 md:self-start">
          <MenuConfig actual={seccion.id} onElegir={(id) => irA(id)} variante="lateral" />
        </aside>
        {/* minmax(0,1fr) y este min-w-0: sin ellos, una tabla ancha estira la
            columna del contenido y empuja el menú fuera de la pantalla. */}
        <div className="min-w-0">
          <Panel key={seccion.id} focusClienteId={focusClienteId} />
        </div>
      </div>
    </div>
  );
}
